// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - To deactivate or hard delete
//           Session Reference Content

import React, {
    useState,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import Modal
    from "@/components/common/modal/Modal"

import {
    useGetSessionReferenceContentByIdQuery,
    useUpdateSessionReferenceContentMutation,
    useDeleteSessionReferenceContentMutation,
} from "./sessionReferenceContentApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Main Component
|--------------------------------------------------------------------------
*/

const EtDeleteSessionReferenceContent = ({
    id,
    setActiveAdminView,
}) => {

    useTitle(
        "Delete Session Reference Content"
    )


    /*
    |--------------------------------------------------------------------------
    | 02 - Fetch Content
    |--------------------------------------------------------------------------
    */

    const {
        data: referenceContent,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetSessionReferenceContentByIdQuery(
        id,
        {
            skip: !id,
        }
    )


    if (
        isLoading ||
        isFetching
    ) {

        return (
            <PulseLoader color="#ea580c" />
        )

    }

    if (isError) {

        return (

            <p className="text-orange-700 text-sm p-3">
                {error?.data?.message ||
                    "Unable to fetch Session Reference Content."}
            </p>

        )

    }

    if (!referenceContent) {

        return (

            <p className="text-gray-600 text-sm p-3">
                Session Reference Content not found.
            </p>

        )

    }


    return (

        <EtDeleteSessionReferenceContentForm
            key={id}
            id={id}
            referenceContent={referenceContent}
            setActiveAdminView={setActiveAdminView}
        />

    )

}


/*
|--------------------------------------------------------------------------
| 03 - Delete Form
|--------------------------------------------------------------------------
*/

const EtDeleteSessionReferenceContentForm = ({
    id,
    referenceContent,
    setActiveAdminView,
}) => {

    const navigate = useNavigate()


    /*
    |--------------------------------------------------------------------------
    | 04 - Modal State
    |--------------------------------------------------------------------------
    */

    const [
        isModalOpen,
        setIsModalOpen,
    ] = useState(false)

    const [
        actionMode,
        setActionMode,
    ] = useState("inactive")


    /*
    |--------------------------------------------------------------------------
    | 05 - Update Mutation
    |--------------------------------------------------------------------------
    */

    const [
        updateSessionReferenceContent,
        {
            isLoading: isUpdating,
            isError: isUpdateError,
            error: updateError,
        },
    ] = useUpdateSessionReferenceContentMutation()


    /*
    |--------------------------------------------------------------------------
    | 06 - Delete Mutation
    |--------------------------------------------------------------------------
    */

    const [
        deleteSessionReferenceContent,
        {
            isLoading: isDeleting,
            isError: isDeleteError,
            error: deleteError,
        },
    ] = useDeleteSessionReferenceContentMutation()


    const isLoading =
        isUpdating ||
        isDeleting

    const isError =
        isUpdateError ||
        isDeleteError

    const error =
        updateError ||
        deleteError


    /*
    |--------------------------------------------------------------------------
    | 07 - Modal Actions
    |--------------------------------------------------------------------------
    */

    const handleOpenModal = mode => {

        setActionMode(mode)

        setIsModalOpen(true)

    }


    const handleCloseModal = () => {

        setIsModalOpen(false)

    }


    /*
    |--------------------------------------------------------------------------
    | 08 - Return To List
    |--------------------------------------------------------------------------
    */

    const returnToList = () => {

        if (setActiveAdminView) {

            setActiveAdminView(
                "sessionReferenceContent"
            )

        } else {

            navigate(
                "/sessionReferenceContent"
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 09 - Confirm Action
    |--------------------------------------------------------------------------
    */

    const handleConfirm = async e => {

        e?.preventDefault?.()

        try {

            /*
            |--------------------------------------------------------------------------
            | A - Hard Delete
            |--------------------------------------------------------------------------
            */

            if (
                actionMode === "hard"
            ) {

                const response =
                    await deleteSessionReferenceContent({
                        id,
                    }).unwrap()

                alert(
                    response?.message ||
                    "Session Reference Content permanently deleted"
                )

                returnToList()

                return

            }


            /*
            |--------------------------------------------------------------------------
            | B - Make Inactive
            |--------------------------------------------------------------------------
            */

            const response =
                await updateSessionReferenceContent({

                    id,

                    active: false,

                }).unwrap()

            alert(
                response?.message ||
                "Session Reference Content made inactive successfully"
            )

            returnToList()

        } catch (submitError) {

            console.error(
                submitError
            )

            alert(
                submitError?.data?.message ||
                (
                    actionMode === "hard"
                        ? "Failed to permanently delete Session Reference Content"
                        : "Failed to deactivate Session Reference Content"
                )
            )

        } finally {

            handleCloseModal()

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 10 - Read Only Field
    |--------------------------------------------------------------------------
    */

    const ReadOnlyField = ({
        label,
        value,
    }) => (

        <div className="bg-white rounded m-1 p-2">

            <p className="font-bold text-xs m-1">
                {label}
            </p>

            <input
                type="text"
                value={value || "Not defined"}
                readOnly
                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
            />

        </div>

    )


    /*
    |--------------------------------------------------------------------------
    | 11 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="bg-gray-200 p-1 w-full md:w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black font-poppins">

            {isLoading && (
                <PulseLoader color="#ea580c" />
            )}

            {isError && (

                <p className="text-orange-700 text-xs p-2">
                    {error?.data?.message}
                </p>

            )}


            <div className="bg-white p-1 m-1 rounded">

                <p className="p-1 m-1 font-bold text-xl">
                    Delete Session Reference Content
                </p>


                <div className="bg-gray-200 rounded m-1 p-1">

                    <form
                        onSubmit={e => {
                            e.preventDefault()
                            handleOpenModal("inactive")
                        }}
                        className="m-1 p-1 text-xs"
                    >


                        {/* Session */}

                        <ReadOnlyField
                            label="Session"
                            value={
                                referenceContent.sessionId
                                    ?.sessionName ||
                                "Session not defined"
                            }
                        />


                        {/* Title */}

                        <ReadOnlyField
                            label="Content Title"
                            value={
                                referenceContent.title
                            }
                        />


                        {/* Type */}

                        <ReadOnlyField
                            label="Content Type"
                            value={
                                referenceContent.contentType
                            }
                        />


                        {/* Provider */}

                        <ReadOnlyField
                            label="Content Provider"
                            value={
                                referenceContent.contentProvider
                            }
                        />


                        {/* Location */}

                        <ReadOnlyField
                            label={
                                referenceContent.contentType === "Video" &&
                                referenceContent.contentProvider === "YouTube"
                                    ? "YouTube Video ID"
                                    : "Content URL"
                            }
                            value={
                                referenceContent.contentType === "Video" &&
                                referenceContent.contentProvider === "YouTube"
                                    ? referenceContent.videoId
                                    : referenceContent.contentUrl
                            }
                        />


                        {/* Reflection Prompts */}

                        <ReadOnlyField
                            label="Reflection Prompts"
                            value={String(
                                referenceContent.reflectionPrompts
                                    ?.length || 0
                            )}
                        />


                        {/* Current Status */}

                        <ReadOnlyField
                            label="Current Status"
                            value={
                                referenceContent.active !== false
                                    ? "Active"
                                    : "Inactive"
                            }
                        />


                        {/* Description */}

                        {referenceContent.description && (

                            <div className="bg-white rounded m-1 p-2">

                                <p className="font-bold text-xs m-1">
                                    Description
                                </p>

                                <p className="bg-gray-100 rounded m-1 p-3 text-xs text-gray-700 whitespace-pre-wrap">
                                    {referenceContent.description}
                                </p>

                            </div>

                        )}


                        {/* Warning */}

                        <div className="bg-orange-50 border border-orange-200 rounded m-1 p-3">

                            <p className="text-xs font-bold text-orange-700">
                                Choose how this reference content
                                should be removed from active use.
                            </p>

                            <p className="text-[10px] text-orange-700 mt-2">
                                Make Inactive preserves the content
                                and its existing cohort assignments,
                                but prevents it from being shown
                                to users.
                            </p>

                            <p className="text-[10px] text-orange-700 mt-2">
                                Hard Delete permanently removes the
                                master content record. Content that
                                has any cohort assignments cannot
                                be hard deleted until those
                                assignments are removed.
                            </p>

                        </div>


                        {/* Actions */}

                        <div className="bg-white m-1 px-1 py-3 rounded">

                            <EtBtnFormBasic
                                name="Make Inactive"
                                type="clear"
                                handleClick={() =>
                                    handleOpenModal(
                                        "inactive"
                                    )
                                }
                            />

                            <EtBtnFormBasic
                                name="Hard Delete"
                                type="create"
                                handleClick={() =>
                                    handleOpenModal(
                                        "hard"
                                    )
                                }
                            />

                            <EtBtnFormBasic
                                name="Cancel"
                                type="cancel"
                                handleClick={returnToList}
                            />

                        </div>

                    </form>

                </div>

            </div>


            {/* Confirmation Modal */}

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirm}
            />

        </div>

    )

}


export default EtDeleteSessionReferenceContent